import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiChevronDown, FiChevronRight, FiList, FiGitBranch, FiRotateCcw } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { getUnlockedFinancialYears } from '../../utils/financialYears';
import {
  getAccountingLedgerGroups,
  getLedgerSubGroups,
  createLedgerSubGroup,
  updateLedgerSubGroup,
  deleteLedgerSubGroup,
  getAllLedgerAccounts,
  getInactiveLedgerAccounts,
  createAccountingLedgerAccount,
  updateAccountingLedgerAccount,
  reactivateLedgerAccount,
  deleteAccountingLedgerAccount,
  permanentDeleteLedgerAccount,
} from '../../services/api';
import { fmtCurrency } from '../../utils/formatNumber';
import LedgerCategorySelect from '../../components/common/LedgerCategorySelect';
import styles from './ChartOfAccounts.module.css';

/** Prevent mouse-wheel scroll from changing a focused number input's value */
const blockWheel = (e) => e.target.blur();

/** Block decimal point / exponent / sign keys so only whole numbers can be typed */
const blockNonInteger = (e) => {
  if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
};

/** Coerce any input to a whole-number string (truncates decimals, e.g. pasted "501.44" → "501") */
const toIntString = (v) => String(v ?? '').split('.')[0].replace(/[^\d]/g, '');

const GROUP_META = {
  asset:       { label: 'Assets',      color: '#3b82f6', normalBalance: 'Dr' },
  liability:   { label: 'Liabilities', color: '#ef4444', normalBalance: 'Cr' },
  income:      { label: 'Income',      color: '#10b981', normalBalance: 'Cr' },
  expenditure: { label: 'Expenses',    color: '#f59e0b', normalBalance: 'Dr' },
  equity:      { label: 'Equity',      color: '#8b5cf6', normalBalance: 'Cr' },
};

const defaultNatureForGroup = (nature) =>
  nature === 'asset' || nature === 'expenditure' ? 'debit' : 'credit';

// -- Build flat row list with rowspan info --------------------------------------
const buildRows = (groups, subGroups, accounts) => {
  const rows = [];
  for (const group of groups) {
    const meta = GROUP_META[group.nature] || {};
    const groupSGs = subGroups
      .filter(sg => String(sg.ledgerGroupId?.id || sg.ledgerGroupId) === String(group.id))
      .sort((a, b) => a.name.localeCompare(b.name));

    if (groupSGs.length === 0) {
      rows.push({ group, meta, groupSpan: 1, sg: null, sgSpan: 1, acc: null });
      continue;
    }

    let groupTotalRows = 0;
    const sgBlocks = groupSGs.map(sg => {
      const sgAccs = accounts
        .filter(a => a.subGroupId && String(a.subGroupId?.id || a.subGroupId) === String(sg.id))
        .sort((a, b) => a.name.localeCompare(b.name));
      const span = Math.max(1, sgAccs.length);
      groupTotalRows += span;
      return { sg, span, accs: sgAccs };
    });

    sgBlocks.forEach(({ sg, span, accs }, sgIdx) => {
      if (accs.length === 0) {
        rows.push({ group, meta, groupSpan: sgIdx === 0 ? groupTotalRows : 0, sg, sgSpan: 1, acc: null });
      } else {
        accs.forEach((acc, accIdx) => {
          rows.push({
            group, meta,
            groupSpan: sgIdx === 0 && accIdx === 0 ? groupTotalRows : 0,
            sg, sgSpan: accIdx === 0 ? span : 0,
            acc,
          });
        });
      }
    });
  }
  return rows;
};

const ChartOfAccountsPage = () => {
  const { activeDerasarId, financialYears, can } = useAuth();
  const unlockedYears = getUnlockedFinancialYears(financialYears || []);

  const [groups,           setGroups]           = useState([]);
  const [subGroups,        setSubGroups]        = useState([]);
  const [accounts,         setAccounts]         = useState([]);
  const [inactiveAccounts, setInactiveAccounts] = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [accountTab,       setAccountTab]       = useState('active'); // 'active' | 'inactive'
  const [viewMode,         setViewMode]         = useState('table');

  // Tree expand state
  const [expandedGroups,    setExpandedGroups]    = useState(new Set());
  const [expandedSubGroups, setExpandedSubGroups] = useState(new Set());

  // Modal types:
  //   'cat-add'       – add category under a group
  //   'cat-edit'      – rename a category
  //   'cat-del'       – delete a category
  //   'acc-add'       – add ledger (subGroupId known — from tree view)
  //   'acc-add-group' – add ledger from group level (pick category first)
  //   'acc-edit'      – edit ledger name/description
  //   'acc-del'       – deactivate ledger
  const [modal,  setModal]  = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!activeDerasarId) return;
    setLoading(true);
    try {
      const [gRes, sgRes, aRes, iRes] = await Promise.all([
        getAccountingLedgerGroups(activeDerasarId),
        getLedgerSubGroups(activeDerasarId),
        getAllLedgerAccounts(activeDerasarId),
        getInactiveLedgerAccounts(activeDerasarId),
      ]);
      const sorted = [...gRes.data].sort((a, b) => a.displayOrder - b.displayOrder);
      setGroups(sorted);
      setSubGroups(sgRes.data);
      setAccounts(aRes.data);
      setInactiveAccounts(iRes.data || []);

      // Tree: auto-expand Assets + first sub-group
      if (sorted.length) {
        const assetGroup = sorted.find(g => g.nature === 'asset') || sorted[0];
        setExpandedGroups(new Set([assetGroup.id]));
        const assetSGs = sgRes.data.filter(
          sg => String(sg.ledgerGroupId?.id || sg.ledgerGroupId) === String(assetGroup.id)
        );
        const def = assetSGs.find(sg => sg.name === 'Bank Account') || assetSGs[0];
        if (def) setExpandedSubGroups(new Set([def.id]));
      }
    } catch {
      toast.error('Failed to load chart of accounts');
    } finally {
      setLoading(false);
    }
  }, [activeDerasarId]);

  useEffect(() => { load(); }, [load]);

  const sgForGroup = (groupId) =>
    subGroups
      .filter(sg => String(sg.ledgerGroupId?.id || sg.ledgerGroupId) === String(groupId))
      .sort((a, b) => a.name.localeCompare(b.name));
  const accForSG = (sgId) =>
    accounts
      .filter(a => a.subGroupId && String(a.subGroupId?.id || a.subGroupId) === String(sgId))
      .sort((a, b) => a.name.localeCompare(b.name));

  const toggleGroup    = id => setExpandedGroups(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSubGroup = id => setExpandedSubGroups(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const patch = (f, v) => setModal(m => {
    const next = { ...m.data, [f]: v };
    if (f === 'ledgerGroupId') next.subGroupId = '';
    if (f === 'subGroupId') {
      // Group is implied by the chosen category; derive it for the opening-balance nature.
      const sg  = subGroups.find(s => String(s.id) === String(v));
      const gid = sg ? String(sg.ledgerGroupId?.id || sg.ledgerGroupId) : '';
      const grp = groups.find(g => String(g.id) === gid);
      next.openingBalanceNature = GROUP_META[grp?.nature]?.normalBalance === 'Cr' ? 'credit' : 'debit';
    }
    return { ...m, data: next };
  });

  // -- Category CRUD ----------------------------------------------------------
  const handleSaveCat = async (e) => {
    e.preventDefault();
    if (modal.type === 'cat-add' && !modal.data.ledgerGroupId) { toast.error('Please select a group'); return; }
    setSaving(true);
    try {
      if (modal.type === 'cat-add') {
        await createLedgerSubGroup({ derasarId: activeDerasarId, ledgerGroupId: modal.data.ledgerGroupId, name: modal.data.name });
        toast.success('Category created');
        if (viewMode === 'tree') setExpandedGroups(p => new Set([...p, modal.data.ledgerGroupId]));
      } else {
        await updateLedgerSubGroup(modal.data.id, { name: modal.data.name });
        toast.success('Category renamed');
      }
      setModal(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDeleteCat = async () => {
    try {
      await deleteLedgerSubGroup(modal.data.id);
      toast.success('Category deleted');
      setModal(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  // -- Ledger CRUD ------------------------------------------------------------
  const handleSaveAcc = async (e) => {
    e.preventDefault();
    const subGroupId = modal.data.subGroupId;
    const isAdd = modal.type === 'acc-add' || modal.type === 'acc-add-group';
    if (isAdd && !subGroupId) { toast.error('Please select a category'); return; }
    const isEdit = modal.type === 'acc-edit';
    if (isEdit && parseFloat(modal.data.openingBalanceAmountInr) > 0 && !modal.data.openingYearId) {
      toast.error('Please select a financial year for the opening balance');
      return;
    }
    setSaving(true);
    try {
      if (modal.type === 'acc-add' || modal.type === 'acc-add-group') {
        await createAccountingLedgerAccount({
          derasarId:               activeDerasarId,
          subGroupId,
          name:                    modal.data.name,
          description:             modal.data.description || undefined,
          openingYearId:           modal.data.openingYearId || undefined,
          openingBalanceAmountInr: modal.data.openingYearId ? (modal.data.openingBalanceAmountInr || 0) : undefined,
          openingBalanceNature:    modal.data.openingYearId ? (modal.data.openingBalanceNature || 'debit') : undefined,
        });
        toast.success('Ledger account created');
        if (viewMode === 'tree') setExpandedSubGroups(p => new Set([...p, subGroupId]));
      } else {
        const updatePayload = {
          description:             modal.data.description,
          openingYearId:           modal.data.openingYearId || null,
          openingBalanceAmountInr: modal.data.openingYearId ? (parseFloat(modal.data.openingBalanceAmountInr) || 0) : undefined,
          openingBalanceNature:    modal.data.openingYearId ? (modal.data.openingBalanceNature || 'debit') : undefined,
        };
        if (!modal.data.isSystem) {
          updatePayload.name = modal.data.name;
        }
        await updateAccountingLedgerAccount(modal.data.id, updatePayload);
        toast.success('Account updated');
      }
      setModal(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDeactivateAcc = async () => {
    try {
      await deleteAccountingLedgerAccount(modal.data.id);
      toast.success('Account deactivated');
      setModal(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handlePermDeleteAcc = async () => {
    try {
      await permanentDeleteLedgerAccount(modal.data.id);
      toast.success('Account permanently deleted');
      setModal(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to delete'); }
  };

  const handleReactivate = async (acc) => {
    try {
      await reactivateLedgerAccount(acc.id);
      toast.success(`${acc.name} reactivated`);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to reactivate'); }
  };

  if (!activeDerasarId) return <div className="empty-state"><p>Select a derasar to view chart of accounts</p></div>;

  // --------------------------------------------------------------------------
  // INACTIVE TAB — flat list of deactivated accounts with reactivate button
  // --------------------------------------------------------------------------
  const renderInactiveView = () => {
    if (inactiveAccounts.length === 0) {
      return (
        <div className="card">
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>
            No deactivated accounts.
          </div>
        </div>
      );
    }
    return (
      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        <div className={styles.tableScroll}>
          <table className={styles.hierTable}>
            <thead>
              <tr>
                <th>Account Name</th>
                <th>Group</th>
                <th>Category</th>
                <th style={{ width: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {inactiveAccounts.map(acc => (
                <tr key={acc.id} className={styles.hierRow} style={{ opacity: 0.7 }}>
                  <td>
                    <span className={styles.accName}>{acc.name}</span>
                    {acc.isSystem && <span className={styles.sysTag}>system</span>}
                    {acc.description && (
                      <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginLeft: 8 }}>
                        {acc.description}
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: '0.83rem' }}>
                    {GROUP_META[acc.group?.nature]?.label || acc.group?.name || '—'}
                  </td>
                  <td style={{ fontSize: '0.83rem' }}>{acc.subGroup?.name || '—'}</td>
                  <td>
                    {can('accountingSetup', 'manage') && (
                      <button
                        className="btn btn-ghost btn-sm"
                        title="Reactivate"
                        onClick={() => handleReactivate(acc)}
                        style={{ color: 'var(--color-success)' }}
                      >
                        <FiRotateCcw size={13} /> Reactivate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // --------------------------------------------------------------------------
  // TABLE VIEW  — 3-column hierarchical table with rowspan
  // --------------------------------------------------------------------------
  const renderTableView = () => {
    const rows = buildRows(groups, subGroups, accounts);

    return (
      <div className="card" style={{ padding: 0, overflow: 'auto' }}>
        <div className={styles.tableScroll}>
        <table className={styles.hierTable}>
          <thead>
            <tr>
              <th className={styles.thGroup}>Group</th>
              <th className={styles.thCat}>Under Category</th>
              <th>Ledger Account</th>
              <th style={{ width: 130 }}>Opening Balance</th>
              <th className={styles.thActions}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className={styles.hierRow}>
                {/* -- Group cell (rowspan) -- */}
                {row.groupSpan > 0 && (
                  <td rowSpan={row.groupSpan} className={styles.groupCell}
                    style={{ borderLeftColor: row.meta.color }}>
                    <div className={styles.groupCellInner}>
                      <div className={styles.groupCellHeader}>
                        <span className={styles.groupName}>{row.meta.label || row.group.name}</span>
                        <span className={styles.groupBadge} style={{ background: row.meta.color }}>
                          {row.meta.normalBalance}
                        </span>
                      </div>
                    </div>
                  </td>
                )}

                {/* -- Category cell (rowspan) -- */}
                {row.sgSpan > 0 && (
                  <td rowSpan={row.sgSpan} className={styles.categoryCell}>
                    {row.sg ? (
                      <div className={styles.catCellInner}>
                        <span className={styles.sgName}>{row.sg.name}</span>
                        {row.sg.isSystem && <span className={styles.sysTag}>system</span>}
                      </div>
                    ) : (
                      <span className={styles.emptyHint}>No categories yet</span>
                    )}
                  </td>
                )}

                {/* -- Account cell -- */}
                <td className={styles.accountCell}>
                  {row.acc ? (
                    <div className={styles.accCellInner}>
                      <span className={styles.accName}>{row.acc.name}</span>
                      {row.acc.isSystem && <span className={styles.sysTag}>system</span>}
                    </div>
                  ) : (
                    <span className={styles.emptyHint}>No ledger accounts yet</span>
                  )}
                </td>

                {/* -- Opening Balance cell -- */}
                <td>
                  {row.acc?.openingBalance ? (
                    <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                      <span style={{ color: row.acc.openingBalance.nature === 'credit' ? 'var(--color-danger)' : 'var(--color-success)', marginRight: 4 }}>
                        {row.acc.openingBalance.nature === 'credit' ? 'Cr' : 'Dr'}
                      </span>
                      {fmtCurrency(row.acc.openingBalance.amountInr)}
                    </span>
                  ) : (
                    <span style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>—</span>
                  )}
                </td>

                {/* -- Actions cell -- */}
                <td className={styles.actionsCell}>
                  {row.acc && can('accountingSetup', 'manage') && (
                    <div className={styles.actions}>
                      <button className="btn btn-ghost btn-sm" title="Edit"
                        onClick={() => setModal({ type: 'acc-edit', data: {
                          id:                      row.acc.id,
                          name:                    row.acc.name,
                          description:             row.acc.description || '',
                          isSystem:                row.acc.isSystem,
                          openingYearId:           row.acc.openingBalance?.financialYearId ? String(row.acc.openingBalance.financialYearId) : '',
                          openingYearLabel:        row.acc.openingBalance?.yearLabel || '',
                          hasExistingOB:           !!row.acc.openingBalance,
                          openingBalanceAmountInr: row.acc.openingBalance?.amountInr != null ? String(row.acc.openingBalance.amountInr) : '',
                          openingBalanceNature:    row.acc.openingBalance?.nature || defaultNatureForGroup(row.group.nature),
                        } })}>
                        <FiEdit2 size={13} />
                      </button>
                      {!row.acc.isSystem && !row.acc.isDonationLinked && (
                        <button className="btn btn-danger btn-sm" title="Deactivate / Delete"
                          onClick={() => setModal({ type: 'acc-manage', data: row.acc })}>
                          <FiTrash2 size={13} />
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    );
  };

  // --------------------------------------------------------------------------
  // TREE VIEW  — expandable accordion
  // --------------------------------------------------------------------------
  const renderTreeView = () => (
    <div className="card" style={{ padding: 0, overflow: 'auto' }}>
      {groups.map(group => {
        const meta = GROUP_META[group.nature] || {};
        const isOpen = expandedGroups.has(group.id);
        const groupSGs = sgForGroup(group.id);
        const groupAccCount = accounts.filter(a => String(a.ledgerGroupId?.id || a.ledgerGroupId) === String(group.id)).length;

        return (
          <div key={group.id} className={styles.groupBlock}>
            <div className={styles.groupRow} style={{ borderLeftColor: meta.color }} onClick={() => toggleGroup(group.id)}>
              <span className={styles.groupToggleIcon}>{isOpen ? <FiChevronDown size={15} /> : <FiChevronRight size={15} />}</span>
              <span className={styles.groupName}>{meta.label || group.name}</span>
              <span className={styles.groupBadge} style={{ background: meta.color }}>{meta.normalBalance}</span>
              <span className={styles.groupMeta}>{groupSGs.length} categor{groupSGs.length !== 1 ? 'ies' : 'y'} · {groupAccCount} account{groupAccCount !== 1 ? 's' : ''}</span>
            </div>

            {isOpen && (
              <div className={styles.sgList}>
                {groupSGs.length === 0
                  ? <div className={styles.emptyHint}>No categories yet</div>
                  : groupSGs.map(sg => {
                    const isSGOpen = expandedSubGroups.has(sg.id);
                    const sgAccounts = accForSG(sg.id);
                    return (
                      <div key={sg.id} className={styles.sgBlock}>
                        <div className={styles.sgRow} onClick={() => toggleSubGroup(sg.id)}>
                          <span className={styles.sgToggleIcon}>{isSGOpen ? <FiChevronDown size={13} /> : <FiChevronRight size={13} />}</span>
                          <span className={styles.sgName}>{sg.name}</span>
                          {sg.isSystem && <span className={styles.sysTag}>system</span>}
                          <span className={styles.sgCount}>{sgAccounts.length}</span>
                          <div className={styles.sgActions} onClick={e => e.stopPropagation()}>
                            {!sg.isSystem && can('accountingSetup', 'manage') && (
                              <>
                                <button className="btn btn-ghost btn-sm" title="Rename"
                                  onClick={() => setModal({ type: 'cat-edit', data: { id: sg.id, name: sg.name } })}>
                                  <FiEdit2 size={12} />
                                </button>
                                <button className="btn btn-danger btn-sm" title="Delete"
                                  onClick={() => setModal({ type: 'cat-del', data: sg })}>
                                  <FiTrash2 size={12} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {isSGOpen && (
                          <div className={styles.accTableWrap}>
                            {sgAccounts.length === 0
                              ? <div className={styles.emptyAccHint}>No accounts under {sg.name}</div>
                              : (
                                <div className={styles.tableScroll}>
                                <table className={styles.accTable}>
                                  <thead>
                                    <tr>
                                      <th>Account Name</th>
                                      <th className={styles.tdDesc}>Description</th>
                                      <th style={{ width: 130 }}>Opening Balance</th>
                                      <th className={styles.thActions}>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {sgAccounts.map(acc => (
                                      <tr key={acc.id} className={styles.accRow}>
                                        <td className={styles.tdName}>
                                          <span className={styles.accName}>{acc.name}</span>
                                          {acc.isSystem && <span className={styles.sysTag}>system</span>}
                                        </td>
                                        <td className={styles.tdDesc}>{acc.description || '—'}</td>
                                        <td>
                                          {acc.openingBalance ? (
                                            <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                                              <span style={{ color: acc.openingBalance.nature === 'credit' ? 'var(--color-danger)' : 'var(--color-success)', marginRight: 4 }}>
                                                {acc.openingBalance.nature === 'credit' ? 'Cr' : 'Dr'}
                                              </span>
                                              {fmtCurrency(acc.openingBalance.amountInr)}
                                            </span>
                                          ) : (
                                            <span style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem' }}>—</span>
                                          )}
                                        </td>
                                        <td className={styles.tdActions}>
                                          <div className={styles.actions}>
                                            {can('accountingSetup', 'manage') && (
                                              <>
                                                <button className="btn btn-ghost btn-sm"
                                                  onClick={() => setModal({ type: 'acc-edit', data: {
                                                    id:                      acc.id,
                                                    name:                    acc.name,
                                                    description:             acc.description || '',
                                                    isSystem:                acc.isSystem,
                                                    openingYearId:           acc.openingBalance?.financialYearId ? String(acc.openingBalance.financialYearId) : '',
                                                    openingYearLabel:        acc.openingBalance?.yearLabel || '',
                                                    openingBalanceAmountInr: acc.openingBalance?.amountInr != null ? String(acc.openingBalance.amountInr) : '',
                                                    openingBalanceNature:    acc.openingBalance?.nature || defaultNatureForGroup(group.nature),
                                                  } })}>
                                                  <FiEdit2 size={13} />
                                                </button>
                                                {!acc.isSystem && !acc.isDonationLinked && (
                                                  <button className="btn btn-danger btn-sm" title="Deactivate / Delete"
                                                    onClick={() => setModal({ type: 'acc-manage', data: acc })}>
                                                    <FiTrash2 size={13} />
                                                  </button>
                                                )}
                                              </>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // -- All categories (searchable) for the acc-add-group modal ----------------
  const categoryOptions = subGroups.map(sg => {
    const gid = String(sg.ledgerGroupId?.id || sg.ledgerGroupId);
    const grp = groups.find(g => String(g.id) === gid);
    return {
      value: sg.id,
      label: sg.name,
      groupNature: grp?.nature,
      groupLabel: grp ? (GROUP_META[grp.nature]?.label || grp.name) : '',
    };
  });

  // --------------------------------------------------------------------------
  return (
    <div className={styles.page}>
      <div className="page-header">
        <div>
          <h2>Chart of Accounts</h2>
          <p className={styles.subtitle}>Group -&gt; Category -&gt; Ledger Account hierarchy</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <div className={styles.viewToggle}>
            <button
              className={`${styles.toggleBtn} ${accountTab === 'active' ? styles.toggleActive : ''}`}
              onClick={() => setAccountTab('active')}
            >
              Active
            </button>
            <button
              className={`${styles.toggleBtn} ${accountTab === 'inactive' ? styles.toggleActive : ''}`}
              onClick={() => setAccountTab('inactive')}
            >
              Inactive {inactiveAccounts.length > 0 && (
                <span style={{ marginLeft: 4, background: 'var(--color-danger)', color: '#fff', borderRadius: 10, padding: '0 6px', fontSize: '0.72rem' }}>
                  {inactiveAccounts.length}
                </span>
              )}
            </button>
          </div>
          {accountTab === 'active' && (
            <div className={styles.viewToggle}>
              <button className={`${styles.toggleBtn} ${viewMode === 'table' ? styles.toggleActive : ''}`} onClick={() => setViewMode('table')}>
                <FiList size={14} /> Table
              </button>
              <button className={`${styles.toggleBtn} ${viewMode === 'tree' ? styles.toggleActive : ''}`} onClick={() => { setViewMode('tree'); setExpandedGroups(new Set(groups.map(g => g.id))); }}>
                <FiGitBranch size={14} /> Tree
              </button>
            </div>
          )}
          {accountTab === 'active' && can('accountingSetup', 'manage') && (
            <>
              <button className="btn btn-ghost btn-sm"
                onClick={() => setModal({ type: 'cat-add', data: { name: '', ledgerGroupId: '' } })}>
                <FiPlus size={14} /> Add Category
              </button>
              <button className="btn btn-primary btn-sm"
                onClick={() => setModal({ type: 'acc-add-group', data: { subGroupId: '', name: '', description: '', openingYearId: '', openingBalanceAmountInr: '', openingBalanceNature: '' } })}>
                <FiPlus size={14} /> Add Ledger
              </button>
            </>
          )}
        </div>
      </div>

      {loading
        ? <div className="loading"><div className="spinner" /></div>
        : accountTab === 'inactive'
          ? renderInactiveView()
          : viewMode === 'table' ? renderTableView() : renderTreeView()
      }

      {/* -- Add / Edit Category Modal -- */}
      {(modal?.type === 'cat-add' || modal?.type === 'cat-edit') && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="card-header">
              <h3>{modal.type === 'cat-add' ? 'New Under Category' : 'Rename Category'}</h3>
            </div>
            <form onSubmit={handleSaveCat}>
              <div className={styles.modalBody}>
                <div className="form-group">
                  <label>Category Name *</label>
                  <input required autoFocus value={modal.data.name}
                    onChange={e => patch('name', e.target.value)}
                    placeholder="e.g., Savings Account, Investment" />
                </div>
                {modal.type === 'cat-add' && (
                  <div className="form-group">
                    <label>Group *</label>
                    <select required value={modal.data.ledgerGroupId} onChange={e => patch('ledgerGroupId', e.target.value)}>
                      <option value="">Select group…</option>
                      {groups.map(g => (
                        <option key={g.id} value={g.id}>{GROUP_META[g.nature]?.label || g.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : modal.type === 'cat-add' ? 'Create' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* -- Add Ledger from group level (pick category) -- */}
      {modal?.type === 'acc-add-group' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className={`modal ${styles.wideModal}`} onClick={e => e.stopPropagation()}>
            <div className="card-header"><h3>New Ledger Account</h3></div>
            <form onSubmit={handleSaveAcc}>
              <div className={styles.modalBody}>
                <div className="form-group">
                  <label>Ledger Name *</label>
                  <input required autoFocus value={modal.data.name}
                    onChange={e => patch('name', e.target.value)}
                    placeholder="e.g., HDFC Current Account" />
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <LedgerCategorySelect
                    value={modal.data.subGroupId}
                    onChange={(id) => patch('subGroupId', id)}
                    options={categoryOptions}
                    placeholder="Select category…"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input value={modal.data.description}
                    onChange={e => patch('description', e.target.value)}
                    placeholder="Optional" />
                </div>
                {unlockedYears.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '0 12px' }}>
                    <div className="form-group">
                      <label>Opening Year <em style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '0.85em' }}>(optional)</em></label>
                      <select value={modal.data.openingYearId} onChange={e => { patch('openingYearId', e.target.value); if (!e.target.value) patch('openingBalanceAmountInr', ''); }}>
                        <option value="">None</option>
                        {unlockedYears.map(y => <option key={y.id} value={y.id}>{y.yearLabel}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Nature</label>
                      <select value={modal.data.openingBalanceNature || 'debit'} onChange={e => patch('openingBalanceNature', e.target.value)} style={{ width: 70 }}>
                        <option value="debit">Dr</option>
                        <option value="credit">Cr</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Opening Balance (₹) <em style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '0.85em' }}>(optional)</em></label>
                      <input
                        type="number" min="0" step="1" placeholder="0"
                        className={styles.noSpin} onWheel={blockWheel}
                        onKeyDown={blockNonInteger}
                        value={modal.data.openingBalanceAmountInr}
                        onChange={e => patch('openingBalanceAmountInr', toIntString(e.target.value))}
                        disabled={!modal.data.openingYearId}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Add / Edit Ledger (from tree — subGroupId already known) ── */}
      {modal?.type === 'acc-add' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="card-header"><h3>New Ledger Account</h3></div>
            <form onSubmit={handleSaveAcc}>
              <div className={styles.modalBody}>
                <div className={styles.contextRow}>
                  <div className={styles.contextItem}>
                    <span className={styles.contextLabel}>Group</span>
                    <span className={styles.contextValue}>{modal.data._groupLabel}</span>
                  </div>
                  <div className={styles.contextItem}>
                    <span className={styles.contextLabel}>Category</span>
                    <span className={styles.contextValue}>{modal.data._sgLabel}</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Ledger Name *</label>
                  <input required autoFocus value={modal.data.name}
                    onChange={e => patch('name', e.target.value)}
                    placeholder="e.g., HDFC Current Account" />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input value={modal.data.description}
                    onChange={e => patch('description', e.target.value)}
                    placeholder="Optional" />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* -- Edit Ledger -- */}
      {modal?.type === 'acc-edit' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="card-header"><h3>Edit Ledger Account</h3></div>
            <form onSubmit={handleSaveAcc}>
              <div className={styles.modalBody}>
                <div className="form-group">
                  <label>Ledger Name *</label>
                  <input required autoFocus value={modal.data.name}
                    readOnly={modal.data.isSystem}
                    style={modal.data.isSystem ? { background: 'var(--color-surface-2)', cursor: 'default' } : undefined}
                    onChange={e => !modal.data.isSystem && patch('name', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input value={modal.data.description}
                    onChange={e => patch('description', e.target.value)}
                    placeholder="Optional" />
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--color-text-muted)', marginBottom: 8 }}>
                    Opening Balance
                  </div>
                  {modal.data.hasExistingOB && (
                    <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: '0 0 8px' }}>
                      Year is fixed once an opening balance is set. Clear the amount and set to None to remove it, then re-add with the correct year.
                    </p>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: '0 12px' }}>
                    <div className="form-group">
                      <label>Year</label>
                      <select
                        value={modal.data.openingYearId || ''}
                        onChange={e => {
                          patch('openingYearId', e.target.value);
                          if (!e.target.value) patch('openingBalanceAmountInr', '');
                        }}
                        style={{ width: 110 }}
                      >
                        <option value="">None</option>
                        {unlockedYears.map(y => (
                          <option key={y.id} value={y.id}>{y.yearLabel}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Nature</label>
                      <select
                        value={modal.data.openingBalanceNature || 'debit'}
                        onChange={e => patch('openingBalanceNature', e.target.value)}
                        style={{ width: 70 }}
                      >
                        <option value="debit">Dr</option>
                        <option value="credit">Cr</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Amount (₹)</label>
                      <input
                        type="number" min="0" step="1"
                        className={styles.noSpin} onWheel={blockWheel}
                        onKeyDown={blockNonInteger}
                        value={modal.data.openingBalanceAmountInr}
                        onChange={e => patch('openingBalanceAmountInr', toIntString(e.target.value))}
                        placeholder="0"
                        disabled={!modal.data.openingYearId}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* -- Delete Category -- */}
      {modal?.type === 'cat-del' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="card-header"><h3>Delete Category</h3></div>
            <div style={{ padding: '16px 20px' }}>
              <p>Delete <strong>{modal.data.name}</strong>?</p>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                All ledger accounts under it must be deactivated first.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeleteCat}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* -- Manage Account (Deactivate / Delete) -- */}
      {modal?.type === 'acc-manage' && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="card-header"><h3>Manage Account</h3></div>
            <div style={{ padding: '16px 20px' }}>
              <p>What would you like to do with <strong>{modal.data.name}</strong>?</p>
              <p style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', margin: '10px 0 0' }}>
                <strong>Deactivate</strong> hides the account — existing entries and balances are preserved.<br />
                <strong>Delete Permanently</strong> removes it forever and only works if no transactions exist.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-ghost" onClick={handleDeactivateAcc}>Deactivate</button>
              <button className="btn btn-danger" onClick={handlePermDeleteAcc}>Delete Permanently</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartOfAccountsPage;
